import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { UniverseService } from './universe.service';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';

export interface PageListElement {
	icon?: string;
	title: string;
	router: string;
	isActive: boolean;
	access: number,
	isSection?: boolean;
	isChat?:boolean;
	universe?: string[];
	canRender:boolean;
}

@Injectable({
	providedIn: 'root'
})
export class PagesService {
	
	translate = inject(TranslateService);
	authService = inject(AuthService);
	private universeService = inject(UniverseService);
	private router = inject(Router);

	public pagesSource;
	public pages$;

	constructor () {


		this.pagesSource = new BehaviorSubject<PageListElement[]>([]);
		this.pages$ = this.pagesSource.asObservable();

		// inicial
		this.buildPages();

		// reaccionar a cambios de idioma
		this.translate.onLangChange.subscribe(() => {
			this.buildPages();
		});

		// Suscribir al evento de cambio de universo
		this.universeService.currentUniverse$
		.subscribe(async (u) => {
			const currentUniverse = u!=null ? u.id_universe : '';
			const updated = this.pagesSource.value.map(p => ({
				...p,
				canRender: this.canRender(p, currentUniverse)
			}));
			this.pagesSource.next(updated);

			// Si estamos en una página con universo, salimos
			const currentPage = updated.find((p) => p.router == this.router.url);
			if (currentPage) {
				if (!currentPage.canRender) {
					this.router.navigate(['/home']);
				}
			}
		});
	}

	private canRender(p:PageListElement, currentUniverse: string) {
		// páginas asociadas al universo
		let canRender = p.universe ? p.universe.includes(currentUniverse) : true;
		// casos especiales
		/*
		if (canRender && p.router == '/events') {
			canRender = environment.production ? false : true;
		}
		*/

		return canRender;
	}

	buildPages() {
		const pageList: PageListElement[] = [];

		// home
		pageList.push({ title: this.translate.instant('sidebar.sections.home'), isSection: true, router: '', isActive: false, access: this.authService.PRIVILEGE_MKT, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.home'), router: '/home', isActive: false, icon: 'home', access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.dashboard'), router: '/dashboard', icon: 'bar_chart', isActive: false, access: this.authService.PRIVILEGE_MKT, canRender: true });

		// Jugadores
		pageList.push({ title: this.translate.instant('sidebar.sections.players'), isSection: true, router: '', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.players'), router: '/players', icon: 'sports_soccer', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.convocations'), router: '/convocations', icon: 'campaign', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.chats'), router: '/chats', icon: 'chat', isActive: false, access: this.authService.PRIVILEGE_USER, isChat: true, canRender: true, universe: ['scouz'] });
		pageList.push({ title: this.translate.instant('sidebar.videos'), router: '/videos', icon: 'video_library', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.ranking'), router: '/ranking', icon: 'leaderboard', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.reports'), router: '/reports', icon: 'assignment', isActive: false, access: this.authService.PRIVILEGE_MKT, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.events'), router: '/events', icon: 'event', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: false });

		// Administración
		pageList.push({ title: this.translate.instant('sidebar.sections.admin'), isSection: true, router: '', isActive: false, access: this.authService.PRIVILEGE_ADMIN, canRender: true, universe: ['scouz'] });
		pageList.push({ title: this.translate.instant('sidebar.notifications'), router: '/notifications', icon: 'send', isActive: false, access: this.authService.PRIVILEGE_MKT, canRender: true, universe: ['scouz'] });
		// pageList.push({ title: this.translate.instant('sidebar.database'), router: '/database', icon: 'backup_table', isActive: false, access: this.authService.PRIVILEGE_SUPERADMIN, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.users'), router: '/users', icon: 'groups', isActive: false, access: this.authService.PRIVILEGE_ADMIN, canRender: true, universe: ['scouz'] });
		pageList.push({ title: this.translate.instant('sidebar.cron'), router: '/cron', icon: 'calendar_clock', isActive: false, access: this.authService.PRIVILEGE_ADMIN, canRender: true, universe: ['scouz'] });
		// pageList.push({ title: this.translate.instant('sidebar.invitations'), router: '/invitations', icon: 'person_add', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		
		// Acciones
		pageList.push({ title: this.translate.instant('sidebar.sections.actions'), isSection: true, router: '', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.settings'), router: '/settings', icon: 'settings', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });
		pageList.push({ title: this.translate.instant('sidebar.logout'), router: '/logout', icon: 'logout', isActive: false, access: this.authService.PRIVILEGE_USER, canRender: true });

		// this.pagesSource = new BehaviorSubject<PageListElement[]>(pageList);
		// this.pages$ = this.pagesSource.asObservable();
		this.pagesSource.next(pageList);
	}
	

	setActive(router: string) {
		if (router=='/') router = '/home';
		const updated = this.pagesSource.value.map(p => ({
			...p,
			isActive: p.router!='' && router.startsWith(p.router)
		}));
		this.pagesSource.next(updated);
	}

	getActivePage(): PageListElement | undefined {
		return this.pagesSource.value.find(p => p.isActive);
	}
}